import { empty, Observable, of } from 'rxjs';
import { Injectable, Inject } from '@angular/core';
import { Router, Params } from '@angular/router';
import { Action, Store, select } from '@ngrx/store';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { map, switchMap, filter, withLatestFrom } from 'rxjs/operators';

import {
  notificationActions,
  NotificationType,
  SnackbarData
} from '@content-platform/web-angular-shared-ui-notifications';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';

import { errorActions } from '../actions';
import { fromError } from '../reducers';

import { Error } from '../models';

import { WebAngularSharedUtilErrorHandlingConfig, WebAngularSharedUtilErrorHandlingOptions } from '../error-handling-config';

import { ErrorDispatchService } from '../error-dispatch.service';
import { ROUTER_CANCEL, RouterCancelAction } from '@ngrx/router-store';
import { getHasError } from '../selectors/error.selectors';

interface RouterStateUrl {
  url: string;
  queryParams: Params;
  params: Params;
}

/**
 * The error effects
 *
 */
@Injectable()
export class ErrorEffects {
  private logger: LoggerService;

  constructor(
    private store: Store<fromError.State>,
    private errorActions$: Actions,
    loggerService: LoggerService,
    @Inject(WebAngularSharedUtilErrorHandlingConfig) private errorHandlingOptions: WebAngularSharedUtilErrorHandlingOptions,
    private errorDispatchService: ErrorDispatchService,
    private router: Router
  ) {
    this.logger = loggerService.instance('NgrxErrorHandler');
  }

  @Effect()
  add$: Observable<Action> = this.errorActions$.pipe(
    ofType(errorActions.ADD_ERROR),
    map((action: errorActions.Add) => action.payload),
    switchMap((error: Error) => {
      this.logger.error(error.message, { source: error.source });
      if (error.redirect) {
        const route = this.errorDispatchService.getRedirectRoute(error);
        if (route) {
          this.router.navigate([route]);
        }
      }
      if (
        error.showNotification &&
        (this.errorHandlingOptions.showNotificationOnRouteTransition ||
          (this.router.url !== this.errorHandlingOptions.errorRedirectRoute &&
            this.router.url !== '/error' &&
            this.router.url !== '/'))
      ) {
        return of(new notificationActions.Open(this.getNotificationObject(error)));
      } else {
        return empty();
      }
    })
  );

  @Effect({ dispatch: false })
  routeFailed$ = this.errorActions$.pipe(
    ofType(ROUTER_CANCEL),
    map((action: RouterCancelAction<RouterStateUrl>) => action.payload),
    withLatestFrom(this.store.pipe(select(getHasError))),
    map(([, hasError]) => {
      if (
        hasError &&
        !this.errorHandlingOptions.doNotRedirectOnErrorGuard &&
        this.router.url === '/'
      ) {
        this.router.navigate([this.errorHandlingOptions.errorRedirectRoute || '/error']);
      }
    })
  );

  @Effect()
  handleAllFailActions$: Observable<Action> = this.errorActions$.pipe(
    filter(
      action =>
        action &&
        action.type.toUpperCase().includes('FAIL') &&
        this.errorHandlingOptions.handleAllFailActions &&
        (<errorActions.Fail>action).isFailAction
    ),
    map((action: errorActions.Add) => action.payload),
    switchMap((error: Error) => {
      return of(new errorActions.Add(error));
    })
  );

  private getNotificationObject(error: Error): SnackbarData {
    return {
      type: NotificationType.Error,
      inputs: {
        props: {
          message: error.message,
          button: {
            label: 'Dismiss',
            action: () => this.store.dispatch(new errorActions.Remove(error))
          }
        }
      }
    };
  }
}
